import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { UserCircleIcon, CommandLineIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../lib/utils'; // Assuming this exists, typical in shadcn. If not I will verify.

interface ChatMessageProps {
    role: 'user' | 'assistant';
    content: string;
    isStreaming?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ role, content, isStreaming }) => {
    const isUser = role === 'user';

    return (
        <div className={cn(
            "flex w-full mb-6 animate-in fade-in slide-in-from-bottom-2 duration-300",
            isUser ? "justify-end" : "justify-start"
        )}>
            <div className={cn(
                "flex max-w-[85%] md:max-w-[75%] gap-3",
                isUser ? "flex-row-reverse" : "flex-row"
            )}>
                {/* Avatar */}
                <div className={cn(
                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm",
                    isUser ? "bg-primary text-primary-foreground" : "bg-white border text-blue-600"
                )}>
                    {isUser ? (
                        <UserCircleIcon className="w-5 h-5" />
                    ) : (
                        <SparklesIcon className="w-5 h-5" />
                    )}
                </div>

                {/* Message Content */}
                <div className="flex flex-col gap-2 min-w-0">
                    {/* Name */}
                    {/* <span className={cn(
                        "text-xs font-medium text-gray-500",
                        isUser ? "text-right" : "text-left"
                    )}>
                        {isUser ? "You" : "MRC AI"}
                    </span> */}

                    <div className={cn(
                        "px-4 py-3 rounded-2xl shadow-sm border text-sm leading-relaxed overflow-hidden",
                        isUser
                            ? "bg-primary text-primary-foreground rounded-tr-none border-primary/20"
                            : "bg-white text-gray-800 rounded-tl-none border-gray-100"
                    )}>
                        {isUser ? (
                            <div className="whitespace-pre-wrap font-sans">{content}</div>
                        ) : (
                            <div className="markdown-body">
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        // Custom styling for markdown elements
                                        p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                                        ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-2 space-y-1" {...props} />,
                                        ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-2 space-y-1" {...props} />,
                                        li: ({ node, ...props }) => <li className="" {...props} />,
                                        a: ({ node, ...props }) => <a className="text-blue-600 hover:underline font-medium" {...props} />,
                                        code: ({ node, className, children, ...props }) => {
                                            const match = /language-(\w+)/.exec(className || '')
                                            return match ? (
                                                <code className="block bg-gray-900 text-gray-100 p-2 rounded-md my-2 text-xs overflow-x-auto" {...props}>
                                                    {children}
                                                </code>
                                            ) : (
                                                <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono border" {...props}>
                                                    {children}
                                                </code>
                                            )
                                        },
                                        blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-blue-200 pl-4 py-1 my-2 bg-blue-50/50 rounded-r italics text-gray-600" {...props} />,
                                        table: ({ node, ...props }) => <div className="overflow-x-auto my-4 rounded-lg border"><table className="w-full text-left text-sm" {...props} /></div>,
                                        thead: ({ node, ...props }) => <thead className="bg-gray-50 border-b" {...props} />,
                                        th: ({ node, ...props }) => <th className="px-4 py-2 font-semibold text-gray-700" {...props} />,
                                        td: ({ node, ...props }) => <td className="px-4 py-2 border-b last:border-0 border-gray-100" {...props} />,
                                    }}
                                >
                                    {content}
                                </ReactMarkdown>
                                {isStreaming && (
                                    <span className="inline-block w-1.5 h-4 ml-1 bg-blue-500 animate-pulse align-middle" />
                                )}
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ChatMessage;
